import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobTemplatesAPI, jobsAPI } from '../api/api';
import { Button } from '../components/ui/button';
import {
  Plus, Trash, PencilSimple, Copy, SpinnerGap, ArrowLeft,
  Lightning, Briefcase, MapPin, CurrencyDollar, Clock, Tag, X, Check
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const JobTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [posting, setPosting] = useState(null);
  const [skillInput, setSkillInput] = useState('');

  const emptyForm = { title: '', description: '', location: '', budget_min: '', budget_max: '', duration: '', required_skills: [] };
  const [form, setForm] = useState(emptyForm);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await jobTemplatesAPI.list();
      if (res.data?.success) setTemplates(res.data.templates || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    if (!token) { navigate('/login?type=contractor'); return; }
    if (userType && userType !== 'contractor') { navigate('/contractor-dashboard'); return; }
    fetchTemplates();
  }, [navigate, fetchTemplates]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast({ title: 'Title and description are required', variant: 'destructive' });
      return;
    }
    try {
      const payload = {
        ...form,
        budget_min: form.budget_min ? Number(form.budget_min) : null,
        budget_max: form.budget_max ? Number(form.budget_max) : null,
      };
      if (editingId) {
        await jobTemplatesAPI.update(editingId, payload);
        toast({ title: 'Template updated' });
      } else {
        await jobTemplatesAPI.create(payload);
        toast({ title: 'Template saved' });
      }
      setCreating(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchTemplates();
    } catch (err) {
      toast({ title: 'Error saving template', description: err.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await jobTemplatesAPI.remove(id);
      toast({ title: 'Template deleted' });
      fetchTemplates();
    } catch { toast({ title: 'Error deleting', variant: 'destructive' }); }
  };

  const handleUseTemplate = async (id) => {
    setPosting(id);
    try {
      const res = await jobTemplatesAPI.use(id);
      if (res.data?.success) {
        const jd = res.data.job_data;
        await jobsAPI.create({
          title: jd.title,
          description: jd.description,
          location: jd.location,
          budget_min: jd.budget_min || 0,
          budget_max: jd.budget_max || 0,
          duration: jd.duration || '',
          required_skills: jd.required_skills || [],
        });
        toast({ title: 'Job posted from template!' });
        fetchTemplates();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.upgrade_required) {
        toast({ title: 'Plan limit reached', description: detail.message, variant: 'destructive' });
      } else {
        toast({ title: 'Error posting job', description: typeof detail === 'string' ? detail : 'Unknown error', variant: 'destructive' });
      }
    } finally { setPosting(null); }
  };

  const startEdit = (t) => {
    setForm({
      title: t.title, description: t.description, location: t.location,
      budget_min: t.budget_min || '', budget_max: t.budget_max || '',
      duration: t.duration || '', required_skills: t.required_skills || [],
    });
    setEditingId(t.id);
    setCreating(true);
  };

  const addSkill = () => {
    if (skillInput.trim() && !form.required_skills.includes(skillInput.trim())) {
      setForm(p => ({ ...p, required_skills: [...p.required_skills, skillInput.trim()] }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setForm(p => ({ ...p, required_skills: p.required_skills.filter(s => s !== skill) }));
  };

  const suggestedSkills = ['Carpentry', 'Plumbing', 'Electrical', 'HVAC', 'Roofing', 'Concrete', 'Drywall', 'Painting', 'Flooring', 'Welding'];

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="job-templates-page">
      {/* Header */}
      <div className="bg-neutral-900 dark:bg-black border-b-4 border-[#2B4C6F]">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <button onClick={() => navigate('/contractor-dashboard')} className="flex items-center gap-2 text-neutral-400 hover:text-white mb-4 transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft size={20} weight="bold" /> Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-white flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center border-2 border-black">
                  <Copy size={22} weight="bold" className="text-white" />
                </div>
                Job Templates
              </h1>
              <p className="text-neutral-400 mt-1">Save jobs you post often. Repost with one click.</p>
            </div>
            {!creating && (
              <Button
                onClick={() => { setCreating(true); setEditingId(null); setForm(emptyForm); }}
                className="h-11 px-5 bg-amber-500 hover:bg-amber-600 text-black font-bold border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
                data-testid="new-template-btn"
              >
                <Plus size={18} weight="bold" className="mr-1" /> New Template
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Create/Edit Form */}
        {creating && (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 mb-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]" data-testid="template-form">
            <h2 className="text-lg font-black text-neutral-900 dark:text-white mb-4">
              {editingId ? 'Edit Template' : 'New Template'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Job Title *</label>
                <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white focus:ring-2 focus:ring-[#2B4C6F]"
                  placeholder="e.g. Residential Electrical Work" data-testid="template-title-input" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Location</label>
                <input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                  className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                  placeholder="e.g. Los Angeles, CA" data-testid="template-location-input" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Description *</label>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white resize-none"
                placeholder="Describe the job requirements..." data-testid="template-description-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Min Budget ($)</label>
                <input type="number" value={form.budget_min} onChange={e => setForm(p => ({ ...p, budget_min: e.target.value }))}
                  className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                  placeholder="5000" data-testid="template-budget-min" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Max Budget ($)</label>
                <input type="number" value={form.budget_max} onChange={e => setForm(p => ({ ...p, budget_max: e.target.value }))}
                  className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                  placeholder="10000" data-testid="template-budget-max" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Duration</label>
                <input value={form.duration} onChange={e => setForm(p => ({ ...p, duration: e.target.value }))}
                  className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                  placeholder="e.g. 2 weeks" data-testid="template-duration" />
              </div>
            </div>
            {/* Skills */}
            <div className="mb-4">
              <label className="block text-xs font-bold uppercase text-neutral-500 mb-1">Required Skills</label>
              <div className="flex gap-2 mb-2">
                <input value={skillInput} onChange={e => setSkillInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
                  className="flex-1 h-9 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                  placeholder="Add a skill" data-testid="template-skill-input" />
                <button onClick={addSkill} className="h-9 px-3 bg-[#2B4C6F] text-white font-bold text-sm rounded-lg border-2 border-black">Add</button>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.required_skills.map(s => (
                  <span key={s} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs font-bold rounded-lg border border-amber-300 dark:border-amber-700">
                    {s} <button onClick={() => removeSkill(s)}><X size={10} weight="bold" /></button>
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-1">
                {suggestedSkills.filter(s => !form.required_skills.includes(s)).map(s => (
                  <button key={s} onClick={() => setForm(p => ({ ...p, required_skills: [...p.required_skills, s] }))}
                    className="px-2 py-0.5 text-xs text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors">
                    + {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleSave}
                className="h-11 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
                data-testid="save-template-btn">
                <Check size={18} weight="bold" className="mr-1" /> {editingId ? 'Update' : 'Save'} Template
              </Button>
              <Button variant="outline" onClick={() => { setCreating(false); setEditingId(null); setForm(emptyForm); }}
                className="h-11 px-4 font-bold border-2 border-neutral-300 dark:border-neutral-600" data-testid="cancel-template-btn">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Templates List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : templates.length === 0 && !creating ? (
          <div className="text-center py-16" data-testid="empty-templates">
            <Copy size={64} weight="bold" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-black text-neutral-700 dark:text-neutral-300 mb-2">No Templates Yet</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-6">Save your first job template for one-click reposting.</p>
            <Button onClick={() => { setCreating(true); setForm(emptyForm); }}
              className="h-11 px-6 bg-amber-500 hover:bg-amber-600 text-black font-bold border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
              data-testid="create-first-template-btn">
              <Plus size={18} weight="bold" className="mr-1" /> Create Template
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="templates-list">
            {templates.map(t => (
              <div key={t.id} className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl overflow-hidden shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]" data-testid={`template-card-${t.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-black text-neutral-900 dark:text-white flex items-center gap-2">
                        <Briefcase size={20} weight="bold" className="text-amber-500" />
                        {t.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {t.location && <span className="flex items-center gap-1"><MapPin size={12} weight="bold" /> {t.location}</span>}
                        {(t.budget_min || t.budget_max) && <span className="flex items-center gap-1"><CurrencyDollar size={12} weight="bold" /> ${(t.budget_min || 0).toLocaleString()} - ${(t.budget_max || 0).toLocaleString()}</span>}
                        {t.duration && <span className="flex items-center gap-1"><Clock size={12} weight="bold" /> {t.duration}</span>}
                        <span className="flex items-center gap-1"><Lightning size={12} weight="bold" /> Used {t.use_count || 0}x</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 ml-3">
                      <button onClick={() => startEdit(t)}
                        className="w-9 h-9 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-lg flex items-center justify-center border border-neutral-300 dark:border-neutral-600 transition-colors"
                        data-testid={`edit-template-${t.id}`}>
                        <PencilSimple size={16} weight="bold" className="text-neutral-600 dark:text-neutral-300" />
                      </button>
                      <button onClick={() => handleDelete(t.id)}
                        className="w-9 h-9 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg flex items-center justify-center border border-red-200 dark:border-red-800 transition-colors"
                        data-testid={`delete-template-${t.id}`}>
                        <Trash size={16} weight="bold" className="text-red-500" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3 line-clamp-2">{t.description}</p>
                  {t.required_skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {t.required_skills.map(s => (
                        <span key={s} className="px-2 py-0.5 bg-[#2B4C6F]/10 text-[#2B4C6F] text-xs font-bold rounded border border-[#2B4C6F]/30 flex items-center gap-1">
                          <Tag size={10} weight="bold" /> {s}
                        </span>
                      ))}
                    </div>
                  )}
                  <button onClick={() => handleUseTemplate(t.id)} disabled={posting === t.id}
                    className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-bold text-sm rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid={`use-template-${t.id}`}>
                    {posting === t.id ? <SpinnerGap size={18} weight="bold" className="animate-spin" /> : <Lightning size={18} weight="bold" />}
                    {posting === t.id ? 'Posting...' : 'Post Job from Template'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobTemplates;
